/**
 * Minimal typed DOM builder.
 *
 * Why this exists rather than template strings + innerHTML: **card content is
 * untrusted**. Collections are shareable, so any text on a card may have been
 * authored by another user — `innerHTML` would make a deck containing
 * `<img src=x onerror=…>` a stored-XSS delivery mechanism for everyone it's
 * shared with. This helper only ever assigns `textContent`, so escaping is
 * structural rather than something each screen has to remember.
 */

type Child = Node | string | number | false | null | undefined;

export interface Props {
  class?: string;
  id?: string;
  /** Set as textContent — never parsed as HTML. */
  text?: string | number;
  dir?: "ltr" | "rtl" | "auto";
  lang?: string;
  hidden?: boolean;
  disabled?: boolean;
  type?: string;
  value?: string;
  placeholder?: string;
  href?: string;
  src?: string;
  title?: string;
  maxLength?: number;
  inputMode?: string;
  autocomplete?: string;
  /** Any remaining attributes, incl. data-* and aria-*. */
  attrs?: Record<string, string | number | boolean | null | undefined>;
  on?: Partial<{
    [K in keyof HTMLElementEventMap]: (event: HTMLElementEventMap[K]) => void;
  }>;
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (props.class) node.className = props.class;
  if (props.id) node.id = props.id;
  if (props.text !== undefined) node.textContent = String(props.text);
  if (props.dir) node.dir = props.dir;
  if (props.lang) node.lang = props.lang;
  if (props.hidden) node.hidden = true;
  if (props.title) node.title = props.title;

  if (node instanceof HTMLButtonElement && props.disabled) node.disabled = true;
  if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
    if (props.value !== undefined) node.value = props.value;
    if (props.placeholder) node.placeholder = props.placeholder;
    if (props.disabled) node.disabled = true;
    if (props.maxLength !== undefined) node.maxLength = props.maxLength;
    if (props.inputMode) node.inputMode = props.inputMode;
    if (props.autocomplete) node.setAttribute("autocomplete", props.autocomplete);
    if (props.type && node instanceof HTMLInputElement) node.type = props.type;
  }
  if (node instanceof HTMLAnchorElement && props.href) node.href = props.href;
  if (node instanceof HTMLImageElement && props.src) node.src = props.src;

  for (const [key, value] of Object.entries(props.attrs ?? {})) {
    if (value === null || value === undefined || value === false) continue;
    node.setAttribute(key, String(value));
  }

  for (const [event, handler] of Object.entries(props.on ?? {})) {
    node.addEventListener(event, handler as EventListener);
  }

  append(node, children);
  return node;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "object" ? child : document.createTextNode(String(child)));
  }
}

export function clear(node: Element): void {
  node.replaceChildren();
}

/** Replace a container's contents in one paint. */
export function mount(host: Element, ...children: Child[]): void {
  const fragment = document.createDocumentFragment();
  append(fragment, children);
  host.replaceChildren(fragment);
}
